import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { useOverviewNavigationList, useOverviewNavigation } from '../../../features/tracks';
import UsageTracker from '../../../utils/UsageTracker';
import ReducedContentCard from '../../ContentCards/ReducedContentCard/ReducedContentCard';
import { OLAF, PANTERA, GREAT_WHITE } from 'HubStyleTokens/colors';
import UIFlex from 'UIComponents/layout/UIFlex';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import { safeSmoothScrollTo } from '../../../utils/utils';
export const OVERVIEW_NAVIGATOR_MAX_WIDTH = 510;
const OVERVIEW_NAVIGATOR_PAGE_SIZE = 12;
const ROW_HEIGHT = 88;
const OverviewNavigatorWrapper = styled(UIFlex).withConfig({
  displayName: "OverviewNavigator__OverviewNavigatorWrapper",
  componentId: "sc-1myeo1h-0"
})(["color:", ";div[class^='ReducedCardWrapper']{padding:14px 16px;border-radius:4px 0 0 4px;margin:0;&:hover{background-color:", ";}&.isSelected{background-color:", ";}&.isSelected,&:hover{color:", ";.certificate-link,.uiLinkDark{color:", ";}}}.certificate-link,.uiLinkDark{text-decoration:underline;color:", ";}"], OLAF, GREAT_WHITE, OLAF, PANTERA, PANTERA, OLAF);
const OverviewNavigatorList = styled.div.withConfig({
  displayName: "OverviewNavigator__OverviewNavigatorList",
  componentId: "sc-1myeo1h-1"
})(["width:", "px;max-width:100%;& > div[class*='UIFlex']{margin:14px 16px;}"], OVERVIEW_NAVIGATOR_MAX_WIDTH);
const ViewMoreButtonWrapper = styled.div.withConfig({
  displayName: "OverviewNavigator__ViewMoreButtonWrapper",
  componentId: "sc-1myeo1h-2"
})(["width:", "px;max-width:100%;"], OVERVIEW_NAVIGATOR_MAX_WIDTH);

const useAutoViewMore = (list, maxDisplay, selectedId, setMaxDisplay) => {
  useEffect(() => {
    if (!list) return;
    const selectedFullListIndex = list.findIndex(row => row.id === selectedId);

    if (selectedFullListIndex > maxDisplay) {
      setMaxDisplay(maxDisplay + OVERVIEW_NAVIGATOR_PAGE_SIZE);
    }
  }, [list, maxDisplay, selectedId, setMaxDisplay]);
};

const useAutoScrollToSelectedRow = (scrollRef, listRef, selectedIndex) => {
  useEffect(() => {
    if (!scrollRef.current || !listRef.current || selectedIndex === -1 || !listRef.current.children[selectedIndex]) return;
    const {
      offsetHeight: scrollHeight,
      scrollTop: currentScroll
    } = scrollRef.current;
    const elementPosition = listRef.current.children[selectedIndex].offsetTop;

    if (elementPosition < currentScroll) {
      safeSmoothScrollTo(scrollRef.current, 0, elementPosition - scrollHeight + ROW_HEIGHT);
    } else if (elementPosition > currentScroll + scrollHeight - ROW_HEIGHT) {
      safeSmoothScrollTo(scrollRef.current, 0, elementPosition);
    }
  }, [scrollRef, listRef, selectedIndex]);
};

const OverviewNavigator = ({
  onClick,
  scrollRef
}) => {
  const [maxDisplay, setMaxDisplay] = useState(OVERVIEW_NAVIGATOR_PAGE_SIZE);
  const {
    list,
    source,
    selectedId,
    selectedType
  } = useOverviewNavigationList();
  const {
    goTo
  } = useOverviewNavigation();
  const listRef = useRef(null);
  const displayedContent = useMemo(() => {
    return list ? list.slice(0, maxDisplay) : [];
  }, [list, maxDisplay]);
  const selectedIndex = useMemo(() => {
    if (!displayedContent || !displayedContent.length) return -1;
    return displayedContent.findIndex(row => row.id === selectedId);
  }, [displayedContent, selectedId]);
  const showViewMoreButton = list != null && maxDisplay < list.length;
  const goToContent = useCallback(content => {
    UsageTracker.track('interaction', {
      action: 'overview-navigator-key',
      contentId: content.id,
      contentType: content.type,
      appearsIn: source
    });
    goTo(content);
  }, [goTo, source]);
  const handleViewMoreClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'overview-navigator-view-more',
      contentId: selectedId,
      contentType: selectedType,
      appearsIn: source
    });
    setMaxDisplay(maxDisplay + OVERVIEW_NAVIGATOR_PAGE_SIZE);
  }, [maxDisplay, selectedId, selectedType, source]);
  const handleKeydown = useCallback(({
    key
  }) => {
    if (selectedIndex === -1) return;

    switch (key) {
      case 'ArrowUp':
        if (selectedIndex > 0) {
          goToContent(displayedContent[selectedIndex - 1]);
        }

        break;

      case 'ArrowDown':
        if (selectedIndex >= displayedContent.length - 2 && showViewMoreButton) handleViewMoreClick();
        if (selectedIndex < displayedContent.length - 1) goToContent(displayedContent[selectedIndex + 1]);
        break;

      default:
    }
  }, [selectedIndex, displayedContent, goToContent, showViewMoreButton, handleViewMoreClick]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);
  useEffect(() => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-navigation-view',
      appearsIn: source
    });
  }, [source]);
  useAutoScrollToSelectedRow(scrollRef, listRef, selectedIndex);
  useAutoViewMore(list, maxDisplay, selectedId, setMaxDisplay);
  if (!list) return null;
  return /*#__PURE__*/_jsxs(OverviewNavigatorWrapper, {
    align: "end",
    direction: "column",
    children: [/*#__PURE__*/_jsx(OverviewNavigatorList, {
      ref: listRef,
      children: displayedContent.map(({
        id,
        type,
        language
      }, index) => /*#__PURE__*/_jsx(ReducedContentCard, {
        appearsIn: source,
        contentId: id,
        contentType: type,
        isSelected: id === selectedId,
        language: language,
        onClick: () => {
          UsageTracker.track('interaction', {
            action: 'overview-navigator-click',
            contentId: id,
            contentType: type,
            appearsIn: source
          });
          onClick();
        },
        position: index + 1
      }, `overview_${id}`))
    }), showViewMoreButton && /*#__PURE__*/_jsx(ViewMoreButtonWrapper, {
      className: "m-top-4 m-bottom-4",
      children: /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        children: /*#__PURE__*/_jsx(UIButton, {
          onClick: handleViewMoreClick,
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.overviewModal.viewMoreButton"
          })
        })
      })
    })]
  });
};

export default OverviewNavigator;